import {
  type Call,
  emphasisIsItalic,
  type Inline,
  runAttributes,
  runLanguage,
  runSegments,
} from '@app/studio'
import { type ReactNode, useContext } from 'react'
import { Note } from './Note'
import { Book, Notes } from './reader-context'

/**
 * An inline run.
 *
 * The language comes from the DATA, so the face and the direction do too — a
 * Unicode range cannot tell pure Hebrew from Hebrew inside an English sentence,
 * and those take different faces.
 *
 * Direction is stated only where the run goes the other way from the BOOK, and
 * stating it is what isolates it: `dir` on an inline element carries
 * `unicode-bidi: isolate`, so a run cannot take its neighbors' punctuation
 * with it. An unmarked run is the book's own language, which is what lets a
 * Hebrew manuscript be written without tagging every paragraph.
 *
 * Such a run still holds letters, and where they are of two scripts each
 * stretch names its own — the `lang` attribute and nothing else, so a screen
 * reader is not handed English to say in Hebrew and the order the words come
 * out in is untouched. `runSegments` is that division, and the page reads it
 * from the same place.
 */
export function InlineRun({
  run,
  calls,
}: {
  run: Inline[]
  /** What this run calls, in order. A marker takes the next one. */
  calls?: Call[]
}) {
  const { bodies, markers, onReveal } = useContext(Notes)
  const book = useContext(Book)
  /* Counted while mapping rather than held as state: this is a pure walk of one
     run, so the same marker lands on the same call every render. */
  let called = 0
  return (
    <>
      {run.map((node, i) => {
        const key = `${i}`
        if (node.t === 'break') return <br key={key} />

        if (node.t === 'call') {
          /* Every call takes its turn, whatever it is: a citation and a
             cross-reference draw nothing, and skipping them would leave every
             marker after one resolving to the wrong note. */
          const call = calls?.[called++]
          if (node.of !== 'note') return null
          /* From the row, because the words no longer say. A call with no row
             draws nothing rather than a number pointing at no note. */
          if (call?.of !== 'note') return null
          const refId = call.id
          const marker = markers.get(refId)
          if (!marker) return null
          return (
            <Note
              key={key}
              marker={marker}
              noteId={refId}
              {...(onReveal ? { onReveal: () => onReveal(refId) } : {})}
            >
              {bodies.get(refId)}
            </Note>
          )
        }

        if (node.t !== 'text') return null

        const lang = runLanguage(book, node.lang)
        const marks = node.marks ?? []
        /* Where the run states no language, each stretch of it states its own
           SCRIPT — the attribute alone, which no bidi rule reads, so the words
           come out in the order they already came out in. */
        const segments = runSegments(book, node.lang, node.v)
        let content: ReactNode = segments
          ? segments.map((s, at) =>
              s.lang ? (
                <span key={`${key}.${at}`} lang={s.lang}>
                  {s.text}
                </span>
              ) : (
                s.text
              ),
            )
          : node.v

        for (const mark of marks) {
          if (mark === 'strong') content = <strong>{content}</strong>
          else if (mark === 'emphasis') {
            // Emphasis is what the author MEANT; italic is only how English
            // says it, and a book answers every mark with weight — Hebrew has
            // no italic, and slanting a Hebrew face distorts letterforms never
            // drawn to lean.
            content = emphasisIsItalic(lang) ? (
              <em>{content}</em>
            ) : (
              <span className="book-em">{content}</span>
            )
          } else if (mark === 'smallcaps')
            content = <span className="book-sc">{content}</span>
          else if (mark === 'transliteration')
            content = <span className="book-tr">{content}</span>
        }

        /* What the page writes, said once and spread here. */
        const { className, ...attrs } = runAttributes(book, node.lang)
        return (
          <span key={key} {...attrs} className={className ?? ''}>
            {content}
          </span>
        )
      })}
    </>
  )
}
